import React, { useState } from "react";
import { Bookmark, ChevronDown, ChevronUp, MoreHorizontal, Trash2 } from "lucide-react";
import CommonDropdownMenu from "@/shared/components/ui/DropDown/DropdownMenu";
import TextButton from "@/shared/components/ui/Button/TextButton";
import { safeFormatTimeAgo } from "@/features/dashboard/utils/accountsDashboardUtils";
import { SAVED_SEARCH_PREVIEW_LIMIT } from "../../constants/prospectConfig";
import type { SavedSearchItem } from "../../types";

const CommonDropdownMenuComponent = CommonDropdownMenu as React.ComponentType<any>;
const TextButtonComponent = TextButton as React.ComponentType<any>;

interface SavedSearchListProps {
  searches: SavedSearchItem[];
  onRun: (search: SavedSearchItem) => void;
  onDelete: (id: string | number) => void;
}

export default function SavedSearchList({ searches, onRun, onDelete }: SavedSearchListProps) {
  const [showAll, setShowAll] = useState(false);
  if (searches.length === 0) return null;

  const visible = showAll ? searches : searches.slice(0, SAVED_SEARCH_PREVIEW_LIMIT);

  return (
    <div className="flex min-h-0 flex-col gap-4">
      <div className="flex shrink-0 items-center justify-between w-full">
        <h5 className="text-sm font-medium text-gray-500">
          Your Saved Searches <span className="text-gray-500">· {searches.length}</span>
        </h5>
        {searches.length > SAVED_SEARCH_PREVIEW_LIMIT && (
          <TextButtonComponent onClick={() => setShowAll((v) => !v)} className="text-sm">
            <span className="flex items-center gap-1">
              {showAll ? "Show fewer" : "Show all"}
              {showAll ? <ChevronUp size={16} strokeWidth={2} /> : <ChevronDown size={16} strokeWidth={2} />}
            </span>
          </TextButtonComponent>
        )}
      </div>

      <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#0f141c] overflow-hidden divide-y divide-gray-100 dark:divide-gray-800/80 shadow-sm min-h-0 max-h-[260px] overflow-y-auto">
        {visible.map((item) => (
          <div
            key={item.id}
            onClick={() => onRun(item)}
            className="flex items-center justify-between gap-4 px-4 py-3.5 hover:bg-gray-50 dark:hover:bg-white/[0.04] transition-colors cursor-pointer group"
          >
            <div className="flex items-center gap-3 min-w-0">
              <Bookmark
                size={16}
                className="shrink-0 text-gray-400 dark:text-gray-500 group-hover:text-secondary transition-colors"
              />
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100 group-hover:text-secondary transition-colors truncate">
                {item.name || item.query}
              </span>
            </div>

            <div className="flex items-center gap-3 shrink-0">
              <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {item.resultCount} results · {safeFormatTimeAgo(item.created_at)}
              </span>
              <div onClick={(e) => e.stopPropagation()}>
                <CommonDropdownMenuComponent
                  actions={[
                    {
                      id: "delete",
                      name: "Delete",
                      isDanger: true,
                      icon: <Trash2 size={14} className="text-red-500" />,
                      onClick: () => onDelete(item.id),
                    },
                  ]}
                  itemId={item.id}
                  triggerBtn={
                    <button
                      type="button"
                      aria-label={`Actions for ${item.name || item.query}`}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                      <MoreHorizontal size={16} />
                    </button>
                  }
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
